/* roleAccess.js — route → allowed roles map.
 *
 * Only the routes listed here have hard gates. Anything not listed defaults to
 * "any authenticated user". The backend remains the ultimate authority (returns
 * 403). This guard is a UX shortcut so unauthorized roles see a friendly
 * "Access Restricted" card instead of a broken/empty page.
 *
 * Admin / super_admin / manager are always allowed (handled in `isRouteAllowed`).
 */

const ADMIN_ALL = ["admin", "super_admin", "manager"];

export const ROUTE_ROLES = {
  // Department schedules — strict, members only
  "/video-editing": [...ADMIN_ALL, "account_manager", "video_editor", "art_director"],
  "/graphic-design": [...ADMIN_ALL, "account_manager", "art_director", "graphic_designer", "paid_ads"],
  "/content-creator": [...ADMIN_ALL, "account_manager", "paid_ads", "content_creator"],
  "/paid-ads-schedule": [...ADMIN_ALL, "account_manager", "paid_ads"],
  // Shooting — open to every internal team member. The full whitelist is
  // expanded so the frontend never blocks anyone the backend allows.
  "/shooting": [...ADMIN_ALL, "account_manager", "photographer", "videographer",
                  "video_editor", "graphic_designer", "art_director",
                  "content_creator", "paid_ads", "sales", "accountant", "finance"],
  "/account-management": [...ADMIN_ALL, "account_manager"],
  "/trash": [...ADMIN_ALL, "manager"],

  // Sensitive sections
  "/finance": [...ADMIN_ALL, "accountant", "finance"],
  "/hr": ADMIN_ALL,
  "/task-templates": ADMIN_ALL,
  "/employees": [...ADMIN_ALL, "account_manager"],
  "/roles": ["admin", "super_admin"],
  "/services": ADMIN_ALL,
  "/quotations": [...ADMIN_ALL, "sales", "account_manager"],
  "/leads": [...ADMIN_ALL, "sales", "account_manager"],
  "/paid-ads": [...ADMIN_ALL, "paid_ads", "account_manager"],
  "/reports": [...ADMIN_ALL, "accountant", "finance", "account_manager"],
};

export function isRouteAllowed(path, role) {
  if (!role) return false;
  if (role === "admin" || role === "super_admin" || role === "manager") return true;
  // Exact match wins; fall back to longest prefix match (e.g. /finance/whatever → /finance)
  let allowed = ROUTE_ROLES[path];
  if (allowed === undefined) {
    const matchKey = Object.keys(ROUTE_ROLES)
      .filter(k => path.startsWith(k + "/") || path === k)
      .sort((a, b) => b.length - a.length)[0];
    allowed = matchKey ? ROUTE_ROLES[matchKey] : undefined;
  }
  // Routes not in the map are open to any authenticated user.
  if (allowed === undefined) return true;
  return Array.isArray(allowed) && allowed.includes(role);
}
